import { useEffect, useState, useCallback, useMemo } from 'react';
import { StepCard } from '../StepCard';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import axios from 'axios';
import { Country, State, City } from 'country-state-city';
import { postcodeValidator } from 'postcode-validator';
import { OnboardingSkeleton } from '../../OnboardingSkeleton';

const sortByName = (a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0);
const findByName = (list, name) => {
  if (!name) return undefined;
  return list.find((x) => x.name.toLowerCase() === name.toLowerCase());
};

export function Address({ onNext, onPrevious, data, onDataChange }) {
  const [formData, setFormData] = useState({
    country: '',
    state: '',
    city: '',
    pincode: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  const getCountryCode = useCallback(() => {
    const country = availableCountries.find((c) => c.name === formData.country);
    return country?.isoCode;
  }, [formData.country, availableCountries]);

  const validatePincode = useCallback(
    (pincode) => {
      try {
        const countryCode = getCountryCode();
        if (!countryCode) return true;
        const isValid = postcodeValidator(pincode, countryCode);
        return isValid;
      } catch (e) {
        console.error('Pincode validation error:', e);
        return true;
      }
    },
    [getCountryCode],
  );

  const fetchGeo = useCallback(async () => {
    try {
      const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch GeoJS');
      const json = await res.json();
      return json;
    } catch (e) {
      console.warn('GeoJS lookup failed', e);
      return null;
    }
  }, []);

  const applyLocation = useCallback(
    (next) => {
      let countryToSet = '';
      let states = [];
      let stateToSet = '';
      let cities = [];
      let cityToSet = '';

      let countryObj =
        (next?.country_code && availableCountries.find((c) => c.isoCode === next.country_code)) ||
        (next?.country && findByName(availableCountries, next.country));

      if (!countryObj) {
        countryObj = availableCountries.find((c) => c.isoCode === 'IN');
      }

      if (countryObj) {
        countryToSet = countryObj.name;
        states = State.getStatesOfCountry(countryObj.isoCode).sort(sortByName);

        const stateObj = findByName(states, next?.region);
        if (stateObj) {
          stateToSet = stateObj.name;
          cities = City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode).sort(sortByName);

          const cityObj = findByName(cities, next?.city);
          if (cityObj) {
            cityToSet = cityObj.name;
          } else if (next?.city) {
            cityToSet = next.city;
          }
        }
      }

      setAvailableStates(states);
      setAvailableCities(cities);
      setFormData((prev) => ({
        ...prev,
        country: countryToSet,
        state: stateToSet,
        city: cityToSet,
      }));
    },
    [availableCountries],
  );

  useEffect(() => {
    const allCountries = Country.getAllCountries().sort(sortByName);
    setAvailableCountries(allCountries);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (isInitialized || availableCountries.length === 0) return;

      const userProfileRaw = typeof window !== 'undefined' ? localStorage.getItem('userProfile') : null;
      const userProfile = userProfileRaw ? JSON.parse(userProfileRaw) : {};

      const pincode = data?.pincode ?? userProfile?.pincode ?? '';
      const primaryCountryName = data?.country ?? userProfile?.country;
      const primaryStateName = data?.state ?? userProfile?.state;
      const primaryCityName = data?.city ?? userProfile?.city;

      if (primaryCountryName) {
        applyLocation({
          country: primaryCountryName,
          region: primaryStateName,
          city: primaryCityName,
        });
        setFormData((prev) => ({ ...prev, pincode }));
      } else {
        const geo = await fetchGeo();
        const nextFromGeo = {
          country_code: geo?.country_code,
          country: geo?.country,
          region: geo?.region,
          city: geo?.city,
        };
        applyLocation(nextFromGeo);
        setFormData((prev) => ({ ...prev, pincode }));
      }

      if (!cancelled) setIsInitialized(true);
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, [availableCountries, data, fetchGeo, isInitialized, applyLocation]);

  useEffect(() => {
    if (!isInitialized) return;
    const selectedCountryObj = availableCountries.find((c) => c.name === formData.country);
    if (selectedCountryObj) {
      const states = State.getStatesOfCountry(selectedCountryObj.isoCode).sort(sortByName);
      setAvailableStates(states);
      const isStateValid = states.some((s) => s.name === formData.state);
      if (!isStateValid && formData.state !== '') {
        setFormData((prev) => ({ ...prev, state: '', city: '' }));
      }
    } else {
      setAvailableStates([]);
      setFormData((prev) => ({ ...prev, state: '', city: '' }));
    }
  }, [formData.country, availableCountries, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
    const selectedCountryObj = availableCountries.find((c) => c.name === formData.country);
    const selectedStateObj = availableStates.find((s) => s.name === formData.state);
    if (selectedCountryObj && selectedStateObj) {
      const cities = City.getCitiesOfState(selectedCountryObj.isoCode, selectedStateObj.isoCode).sort(sortByName);
      setAvailableCities(cities);
      const isCityValid = cities.some((c) => c.name === formData.city);
      if (!isCityValid && formData.city !== '') {
        setFormData((prev) => ({ ...prev, city: '' }));
      }
    } else {
      setAvailableCities([]);
      setFormData((prev) => ({ ...prev, city: '' }));
    }
  }, [formData.state, formData.country, availableStates, availableCountries, isInitialized]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (formData.pincode && !validatePincode(formData.pincode)) {
      setErrors({ pincode: 'Please enter a valid Pincode for the selected country.' });
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        country: formData.country,
        state: formData.state,
        city: formData.city,
        pincode: formData.pincode,
      };

      const response = await axios.patch(`/api/user/profile/`, updateData);

      if (response.status === 200) {
        localStorage.setItem('userProfile', JSON.stringify(response.data.data.user));
        onDataChange(response.data.data.user);
        onNext();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to save data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === 'country') {
        newState.state = '';
        newState.city = '';
      } else if (field === 'state') {
        newState.city = '';
      }
      return newState;
    });
    if (field === 'pincode' && errors.pincode) {
      setErrors((prev) => ({ ...prev, pincode: undefined }));
    }
  };

  if (!isInitialized) {
    return <OnboardingSkeleton />;
  }

  return (
    <StepCard>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">
            Please Confirm Your Address
          </h2>
          <p className="text-[var(--q3-danger-normal)] text-sm">
            We have filled your address with your IP, Please update if required
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <Select
              value={formData.country}
              onValueChange={(value) => {
                handleSelectChange('country', value);
              }}
            >
              <SelectTrigger className="border-[var(--q3-stroke-normal)] h-[40px] w-full overflow-hidden rounded-full flex justify-between items-center px-4">
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[var(--q3-neutral-light)]">Country :</span>
                  <SelectValue className="truncate text-ellipsis max-w-full" placeholder="Select Country" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-3xl">
                {availableCountries.map((country) => (
                  <SelectItem
                    className="cursor-pointer rounded-3xl hover:bg-[var(--q3-surface-dimmer)]"
                    key={country.isoCode}
                    value={country.name}
                  >
                    <span className="text-[var(--q3-neutral-default)]">
                      {country.flag} {country.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={formData.state}
              onValueChange={(value) => {
                handleSelectChange('state', value);
              }}
              disabled={!formData.country}
            >
              <SelectTrigger className="border-[var(--q3-stroke-normal)] h-[40px] w-full rounded-full flex justify-between items-center px-4">
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[var(--q3-neutral-light)]">State :</span>
                  <SelectValue placeholder="Select State" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-3xl">
                {availableStates.map((state) => (
                  <SelectItem
                    className="cursor-pointer rounded-3xl hover:bg-[var(--q3-surface-dimmer)]"
                    key={state.isoCode}
                    value={state.name}
                  >
                    <span className="text-[var(--q3-neutral-default)]">{state.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={formData.city}
              onValueChange={(value) => {
                handleSelectChange('city', value);
              }}
              disabled={!formData.state}
            >
              <SelectTrigger className="border-[var(--q3-stroke-normal)] h-[40px] w-full rounded-full flex justify-between items-center px-4">
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-[var(--q3-neutral-light)]">City :</span>
                  <SelectValue placeholder="Select City" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-3xl">
                {availableCities.map((city) => (
                  <SelectItem
                    className="cursor-pointer rounded-3xl hover:bg-[var(--q3-surface-dimmer)]"
                    key={city.name}
                    value={city.name}
                  >
                    <span className="text-[var(--q3-neutral-default)]">{city.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative h-[40px] w-full rounded-full border border-input bg-[var(--q3-surface-default)] px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center">
              {formData.pincode && <span className="shrink-0 mr-1 text-[var(--q3-neutral-light)]">Pincode :</span>}
              <Input
                type="text"
                placeholder={formData.pincode ? '' : 'Enter Pincode'}
                value={formData.pincode}
                onChange={(e) => handleSelectChange('pincode', e.target.value)}
                className="flex-1 h-full w-full !bg-transparent border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none outline-none p-0 rounded-none"
              />
            </div>
            {errors.pincode && (
              <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.pincode}</div>
            )}
          </div>

          {errors.submit && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.submit}</div>}

          <div className="flex flex-col sm:flex-row pt-4 gap-4 sm:gap-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onPrevious}
              className="w-full sm:flex-shrink-0 sm:flex-grow-0 sm:basis-1/3 px-4 sm:px-8 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 text-[var(--q3-neutral-default)] disabled:opacity-50 border border-[var(--q3-stroke-normal)]"
              disabled={isLoading}
            >
              Previous
            </Button>
            <Button
              type="submit"
              className="w-full sm:flex-shrink-0 sm:flex-grow sm:basis-2/3 bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] disabled:opacity-50 px-4 sm:px-8 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save & Next'}
            </Button>
          </div>
        </form>
      </div>
    </StepCard>
  );
}
